import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, RefreshCw, CheckCircle2, Share2, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI-driven tool analysis card. Calls Claude Sonnet 4.5 (via Emergent LLM Key)
 * server-side, server caches per (tool + inputs + language).
 *
 * Props:
 *   tool: one of "hay" | "cost" | "farrier" | "foaling" | "trailer"
 *   inputs: dict matching the tool's prompt placeholders
 *   accent: tailwind color name for the accent (e.g. "amber", "emerald")
 */
const AiAnalysisCard = ({ tool, inputs, accent = "indigo" }) => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [state, setState] = useState({ loading: false, analysis: null, tips: [], error: null, cached: false, shareToken: "" });
  const [copied, setCopied] = useState(false);

  const fetchAnalysis = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await axios.post(`${API}/tool-analysis`, { tool, inputs, language: lang });
      setState({ loading: false, analysis: data.analysis, tips: data.tips || [], error: null, cached: data.cached, shareToken: data.share_token || "" });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || (isNl ? "Er ging iets mis. Probeer het later opnieuw." : "Something went wrong. Please try again later.") }));
    }
  };

  const reset = () => { setState({ loading: false, analysis: null, tips: [], error: null, cached: false, shareToken: "" }); setCopied(false); };

  const handleShare = async () => {
    if (!state.shareToken) return;
    const url = `${window.location.origin}/share/${state.shareToken}`;
    const title = isNl ? "Bekijk mijn paarden-analyse" : "Check out my horse analysis";
    const text = state.analysis.slice(0, 140) + "…";
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); return; } catch (_) { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (_) { /* noop */ }
  };

  const colors = {
    amber: { bg: "from-amber-50 to-white", ring: "border-amber-300", btn: "bg-amber-600 hover:bg-amber-700", text: "text-amber-900", chip: "bg-amber-100 text-amber-700" },
    emerald: { bg: "from-emerald-50 to-white", ring: "border-emerald-300", btn: "bg-emerald-600 hover:bg-emerald-700", text: "text-emerald-900", chip: "bg-emerald-100 text-emerald-700" },
    stone: { bg: "from-stone-50 to-white", ring: "border-stone-300", btn: "bg-stone-700 hover:bg-stone-900", text: "text-stone-900", chip: "bg-stone-100 text-stone-700" },
    pink: { bg: "from-pink-50 to-white", ring: "border-pink-300", btn: "bg-pink-600 hover:bg-pink-700", text: "text-pink-900", chip: "bg-pink-100 text-pink-700" },
    blue: { bg: "from-blue-50 to-white", ring: "border-blue-300", btn: "bg-blue-600 hover:bg-blue-700", text: "text-blue-900", chip: "bg-blue-100 text-blue-700" },
    indigo: { bg: "from-indigo-50 to-white", ring: "border-indigo-300", btn: "bg-indigo-600 hover:bg-indigo-700", text: "text-indigo-900", chip: "bg-indigo-100 text-indigo-700" },
  };
  const C = colors[accent] || colors.indigo;

  if (!state.analysis && !state.loading && !state.error) {
    return (
      <div className={`bg-gradient-to-br ${C.bg} border-2 border-dashed ${C.ring} rounded-2xl p-6 text-center`} data-testid={`ai-card-${tool}-prompt`}>
        <Sparkles size={28} className={`${C.text} mx-auto mb-3 opacity-70`} />
        <h3 className={`font-bold text-lg ${C.text} mb-1`}>{isNl ? "Wil je persoonlijk AI-advies?" : "Want personalised AI analysis?"}</h3>
        <p className={`text-sm ${C.text} opacity-80 mb-4`}>{isNl ? "Onze AI kijkt naar jouw exacte invoer en geeft maatwerk-advies." : "Our AI looks at your exact inputs and gives tailored advice."}</p>
        <button onClick={fetchAnalysis} className={`${C.btn} text-white px-5 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-colors`} data-testid={`ai-card-${tool}-btn`}>
          <Sparkles size={14} /> {isNl ? "Genereer mijn analyse" : "Generate my analysis"}
        </button>
        <p className="text-[10px] text-[#94A3B8] mt-3">{isNl ? "Gratis · ~10s · Claude AI" : "Free · ~10s · Claude AI"}</p>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className={`bg-gradient-to-br ${C.bg} border-2 ${C.ring} rounded-2xl p-6`} data-testid={`ai-card-${tool}-loading`}>
        <div className="flex items-center gap-3">
          <Loader2 size={24} className={`${C.text} animate-spin`} />
          <div>
            <div className={`font-bold ${C.text}`}>{isNl ? "AI denkt na…" : "AI is thinking…"}</div>
            <div className={`text-xs ${C.text} opacity-70`}>{isNl ? "Persoonlijk advies wordt opgesteld (5-15s)" : "Drafting your personal advice (5-15s)"}</div>
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5" data-testid={`ai-card-${tool}-error`}>
        <p className="text-red-900 text-sm mb-2">{state.error}</p>
        <button onClick={fetchAnalysis} className="text-xs text-red-700 underline">{isNl ? "Opnieuw proberen" : "Try again"}</button>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br ${C.bg} border-2 ${C.ring} rounded-2xl p-6`} data-testid={`ai-card-${tool}-result`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className={`inline-flex items-center gap-1.5 ${C.chip} px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest`}>
          <Sparkles size={11} /> {isNl ? "AI-analyse" : "AI analysis"}
          {state.cached && <span className="opacity-70">· {isNl ? "cache" : "cached"}</span>}
        </div>
        <button onClick={reset} className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1" data-testid={`ai-card-${tool}-reset`}>
          <RefreshCw size={11} /> {isNl ? "Opnieuw" : "Reset"}
        </button>
      </div>
      <p className={`text-sm ${C.text} leading-relaxed whitespace-pre-line mb-4`} data-testid={`ai-card-${tool}-analysis`}>{state.analysis}</p>
      {state.tips.length > 0 && (
        <ul className="space-y-1.5 border-t border-white/50 pt-3">
          {state.tips.map((t, i) => (
            <li key={i} className={`flex items-start gap-2 text-xs ${C.text}`}>
              <CheckCircle2 size={14} className="shrink-0 mt-0.5 opacity-70" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      )}
      {state.shareToken && (
        <div className="mt-4 pt-3 border-t border-white/50 flex items-center justify-between gap-3">
          <button onClick={handleShare} className={`${C.btn} text-white px-4 py-2 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors`} data-testid={`ai-card-${tool}-share`}>
            {copied ? <><Check size={12} /> {isNl ? "Link gekopieerd" : "Link copied"}</> : <><Share2 size={12} /> {isNl ? "Deel mijn analyse" : "Share my analysis"}</>}
          </button>
          <span className={`text-[10px] ${C.text} opacity-60`}>{isNl ? "Help vrienden vergelijken" : "Help friends compare"}</span>
        </div>
      )}
    </div>
  );
};

export default AiAnalysisCard;
